import React from "react";
import { useNavigate } from "react-router-dom";
import { Navigation, Heart, Code, Shield, Cpu, Users } from "lucide-react";
import AdBanner from "../components/AdBanner";
import useSeo from "../seo/useSeo";

export default function AboutUs() {
  useSeo("/about-us");
  const navigate = useNavigate();

  return (
    <div className="min-h-[100dvh] bg-slate-950 text-white pb-24 selection:bg-primary selection:text-white">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 p-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : navigate("/")}
            className="p-2 -ml-2 rounded-full hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
          >
            ←
          </button>
          <h1 className="text-lg font-bold">About Us</h1>
        </div>
      </div>

      <div className="p-5 max-w-3xl mx-auto space-y-10 mt-8">
        {/* Brand Icon */}
        <div className="text-center">
          <div className="w-20 h-20 bg-primary rounded-3xl flex items-center justify-center mx-auto shadow-lg shadow-primary/20 mb-6 transform rotate-3">
            <Navigation size={40} className="text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight">
            Built for Security. <br />
            <span className="bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">
              Designed for Vehicle Owners.
            </span>
          </h2>
        </div>

        {/* Deep, High-Quality Content Paragraphs for AdSense Approval */}
        <div className="space-y-6 text-slate-300 text-sm md:text-base leading-relaxed">
          <section className="bg-slate-900/40 border border-slate-800/60 rounded-3xl p-6 md:p-8 space-y-4">
            <div className="flex items-center gap-2.5 text-primary font-bold">
              <Users size={20} />
              <span>1. Our Origin & Vision</span>
            </div>
            <p>
              CarTrack was founded on a simple yet powerful premise: advanced automotive security should be accessible to everyday drivers and fleet owners alike, without the burden of expensive proprietary tracking hardware or recurring monthly subscription fees. Traditional GPS trackers require complex wiring, obtrusive OBD-port devices, and locked-in platform contracts. We envisioned a better way—leveraging the advanced Android smart screens already installed in millions of modern car dashboards to serve as high-performance IoT tracking consoles.
            </p>
          </section>

          <section className="bg-slate-900/40 border border-slate-800/60 rounded-3xl p-6 md:p-8 space-y-4">
            <div className="flex items-center gap-2.5 text-blue-400 font-bold">
              <Cpu size={20} />
              <span>2. Pioneering IoT Architecture</span>
            </div>
            <p>
              Under the hood, CarTrack operates on a robust, state-of-the-art dual-application architecture. The tracking engine runs entirely as an optimized background service on Android Car Head Units, maintaining location telemetry capture even under low memory thresholds and during system sleep states. This client transmits high-fidelity coordinate arrays, ignition states, and speed metrics via encrypted TLS protocols to our high-performance backend. The data is instantly processed, delivering real-time map positions, weekly driver safety scoring, and instant geofence breach updates.
            </p>
          </section>

          <section className="bg-slate-900/40 border border-slate-800/60 rounded-3xl p-6 md:p-8 space-y-4">
            <div className="flex items-center gap-2.5 text-red-400 font-bold">
              <Heart size={20} />
              <span>3. Instant Telegram Integration</span>
            </div>
            <p>
              We believe alerts are only useful if they reach you immediately. By bypassing sluggish standard app notifications, CarTrack integrates directly with the Telegram messaging ecosystem. The second your car screen registers an engine start, an overspeed event, or an emergency SOS trigger, our Telegram gateway instantly dispatches interactive maps, live coordinates, and status descriptions directly to your linked personal chat. This guarantees that you are in total control of your vehicle's safety, no matter where you are in the world.
            </p>
          </section>

          <section className="bg-slate-900/40 border border-slate-800/60 rounded-3xl p-6 md:p-8 space-y-4">
            <div className="flex items-center gap-2.5 text-green-400 font-bold">
              <Shield size={20} />
              <span>4. Uncompromising Data Sovereignty</span>
            </div>
            <p>
              Unlike standard commercial tracking platforms that profit by silently selling your driving habits, travel telemetry, and frequent destinations to third-party data brokers, CarTrack prioritizes strict user privacy. Your database is completely isolated. Furthermore, we believe you should have total authority over your records. We provide a single-tap 'Delete Account' utility within the Owner Dashboard, allowing you to instantly and permanently erase your user profile, linked vehicles, entire coordinate histories, alerts logs, and linked contact lists from our servers in real-time.
            </p>
          </section>
        </div>

        {/* Harmonious Visual Cards */}
        <div className="grid grid-cols-2 gap-4 mt-8">
          <div className="bg-slate-900 rounded-3xl p-5 border border-slate-800 flex flex-col items-center text-center">
            <Heart size={24} className="text-red-500 mb-3 animate-pulse" />
            <h4 className="font-bold mb-1">Passionate Team</h4>
            <p className="text-xs text-slate-400 leading-relaxed">Dedicated to developing accessible vehicle security systems</p>
          </div>
          <div className="bg-slate-900 rounded-3xl p-5 border border-slate-800 flex flex-col items-center text-center">
            <Code size={24} className="text-blue-500 mb-3" />
            <h4 className="font-bold mb-1">Modern Tech</h4>
            <p className="text-xs text-slate-400 leading-relaxed">Fast, low-latency, and battery-optimized background tracking</p>
          </div>
        </div>

        {/* Compliant Google AdSense Banner */}
        <div className="mt-8 border-t border-slate-800 pt-6">
          <AdBanner testid="about-ad-banner" />
        </div>

        {/* Copyright Footer */}
        <div className="mt-12 pt-8 border-t border-slate-800 text-center">
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} CarTrack. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
